import { auth } from '../firebase/config';
import { signInWithCustomToken } from 'firebase/auth';
import { CORE_API_URL } from '../api/api';
import { IS_LOCAL_HARNESS, readLocalSession } from '../config/localSession';

const AUTH_STATE_CHANGE_EVENT = 'authStateChange';

/** Fired by the 401 interceptor once a refreshed token has not brought the session back. */
export const SESSION_EXPIRED_EVENT = 'sessionExpired';

/** What a student reads wherever a request failed because they are no longer signed in. */
export const SIGNED_OUT_LINE = 'You have been signed out. Sign in again to continue.';

/** Whether a failed request failed because the session has ended (see `api/api.js`). */
export const isSignedOutError = (error) => Boolean(error?.signedOut);

/** The sign-in page, set to come back to the page this is called on. */
export const signInAgainHref = () => {
    const here = `${window.location.pathname}${window.location.search}`;
    return `/login?returnTo=${encodeURIComponent(here)}`;
};

export const getEmail = () => {
    return localStorage.getItem('email');
};

export const getUserType = () => {
    return localStorage.getItem('user_type');
};

export const getUserId = () => {
    return localStorage.getItem('user_id');
};

/**
 * Store user context data (not auth tokens — the session cookie handles auth).
 */
export const setAuthData = (email, userType, userId) => {
    if (email) localStorage.setItem('email', email);
    if (userType) localStorage.setItem('user_type', userType);
    if (userId) localStorage.setItem('user_id', userId);
    window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
};

/**
 * Clear all user context data.
 */
export const clearAuthData = () => {
    localStorage.removeItem('email');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_id');
    window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
};

/**
 * Check if user is authenticated (has stored user context).
 * The actual session validity is enforced server-side via the httponly cookie.
 */
export const isAuthenticated = () => {
    return !!getEmail();
};

/**
 * Sign into the Firebase SDK using the session cookie.
 * Calls the core-api /auth/firebase-token endpoint which validates the
 * session and returns a Firebase custom token, then uses it to authenticate
 * with the Firebase client SDK.
 */
export const signInWithSessionCookie = async () => {
    // No core-api in the local harness, so no custom token to exchange. The pages that
    // want Firestore take their documented API fallback, exactly as they do for anyone
    // whose Firebase sign-in did not complete.
    if (IS_LOCAL_HARNESS) return false;

    try {
        const response = await fetch(`${CORE_API_URL}/auth/firebase-token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
            return false;
        }

        const data = await response.json();

        if (auth && data.firebase_token) {
            await signInWithCustomToken(auth, data.firebase_token);
            return true;
        }

        return false;
    } catch {
        return false;
    }
};

/**
 * The user the session cookie belongs to, read fresh from core-api rather than from
 * localStorage, which still names the previous user until `validateSessionOnLoad`
 * lands. Falls back to the stored id when the read fails.
 * @returns {Promise<string>}
 */
export const readSessionUserId = async () => {
    if (IS_LOCAL_HARNESS) return readLocalSession()?.user_id || '';

    try {
        const response = await fetch(`${CORE_API_URL}/auth/me`, {
            method: 'GET',
            credentials: 'include',
        });
        if (response.ok) {
            const data = await response.json();
            if (data?.user_id) return String(data.user_id);
        }
    } catch {
        // The stored id below.
    }
    return getUserId() || '';
};

let identityCheck = null;

/**
 * Make the Firebase sign-in this tab holds belong to the session's user (B13).
 *
 * The SDK restores whoever last signed in on this browser, and `validateSessionOnLoad`
 * only signs in when nobody is — so after a different account logs in, chat listens
 * as the old uid and every lane comes back empty with no error. On a mismatch, or no
 * sign-in at all, this signs in again through the session cookie. One check at a time.
 *
 * @returns {Promise<{ok: boolean, uid: string, sessionUserId: string}>} - `uid` is the
 *   Firebase uid when `ok`
 */
export const ensureFirebaseMatchesSession = () => {
    if (identityCheck) return identityCheck;

    identityCheck = (async () => {
        const sessionUserId = await readSessionUserId();
        if (!auth) return { ok: false, uid: '', sessionUserId };

        await auth.authStateReady?.().catch(() => {});
        const matches = () => Boolean(auth.currentUser?.uid)
            && (!sessionUserId || auth.currentUser.uid === sessionUserId);

        if (!matches()) await signInWithSessionCookie();
        return matches()
            ? { ok: true, uid: auth.currentUser.uid, sessionUserId }
            : { ok: false, uid: '', sessionUserId };
    })().finally(() => { identityCheck = null; });

    return identityCheck;
};

/**
 * Validate the user session on page load by calling /auth/me.
 * This hydrates localStorage from the session cookie, enabling
 * cross-subdomain single sign-on (cookie is shared across
 * *.applisolutions.com but localStorage is per-origin).
 *
 * After hydrating user context, ensures the Firebase SDK is signed in.
 */
export const validateSessionOnLoad = async () => {
    // The local harness has no session cookie to hydrate from: the session is whatever
    // the browser already holds, put there by the stubbed sign-in on the login page.
    if (IS_LOCAL_HARNESS) {
        const session = readLocalSession();
        if (!session) {
            clearAuthData();
            return false;
        }
        setAuthData(session.email, session.user_type, session.user_id);
        return true;
    }

    try {
        const response = await fetch(`${CORE_API_URL}/auth/me`, {
            method: 'GET',
            credentials: 'include',
        });

        if (!response.ok) {
            clearAuthData();
            return false;
        }

        const data = await response.json();
        setAuthData(data.email, data.user_type, data.user_id);

        if (!auth?.currentUser) {
            await new Promise((resolve) => {
                if (!auth) { resolve(); return; }
                const unsubscribe = auth.onAuthStateChanged(() => {
                    unsubscribe();
                    resolve();
                });
                setTimeout(() => { unsubscribe(); resolve(); }, 3000);
            });

            if (!auth?.currentUser) {
                await signInWithSessionCookie();
            }
        }

        return true;
    } catch {
        clearAuthData();
        return false;
    }
};
